import React, {useEffect} from 'react';

import classNames from 'classnames';
import CSSModules from 'react-css-modules';

import {useWindowSize} from '../../../hooks/windowSize';
import Heading from '../../../TextComponents/Heading';
import Text from '../../../TextComponents/Text';
import type {TextColours} from '../../../TextComponents/textUtils';
import {createMarkup, renderMarkdown} from '../../../utils/markdownUtils';
import PreviewNavButton from '../PreviewNavButton';
import ImageContent from './ImageContent';

import styles from '../styles.module.scss';

type TextSlideProps = {
  title?: string;
  description?: string;
  image: {
    url: string;
    position: 'top' | 'bottom';
  };
  imageStyle: {[key: string]: string};
  background: string;
  textColor?: TextColours;
  transition?: boolean;
  editMode?: boolean;
  reskinClassName?: string;
  button: {
    text: string;
  };
  isButtonLightTheme?: boolean;
  isPreviewMode?: boolean;
  isUsingHtmlEditor?: boolean;
};

// eslint-disable-next-line complexity
const TextSlide = ({
  title,
  description,
  image,
  imageStyle,
  background,
  textColor,
  transition = false,
  editMode = false,
  reskinClassName = '',
  button,
  isButtonLightTheme = false,
  isPreviewMode = false,
  isUsingHtmlEditor = false,
}: TextSlideProps): JSX.Element => {
  const windowSize = useWindowSize();
  const textContainerClass = image ? 'half-container' : 'full-container';
  const previewContainerClass = isPreviewMode ? 'preview-mode' : '';
  const scrollingContainerHeightStyle =
    !editMode && windowSize.width >= 720 ? {height: windowSize.height} : {};

  const renderedDescription = isUsingHtmlEditor
    ? description || ''
    : renderMarkdown(description || '');

  const textContent = (
    <div
      styleName={classNames(
        textContainerClass,
        previewContainerClass,
        'text-container'
      )}
    >
      <div
        styleName='content-scrolling-container'
        style={scrollingContainerHeightStyle}
      >
        <div styleName='inner-container'>
          {title ? (
            <Heading
              as='h1'
              size='sheep'
              weight='medium'
              colour={textColor}
              dataAutomation={`title-text${editMode ? '-preview' : ''}${
                isPreviewMode ? '-thumbnail' : ''
              }`}
            >
              {title}
            </Heading>
          ) : null}

          <div
            styleName={classNames('text-body', {
              'remove-line-spacing': isUsingHtmlEditor,
            })}
          >
            <Text
              size='xl'
              colour={textColor}
              dataAutomation={`description-text${editMode ? '-preview' : ''}${
                isPreviewMode ? '-thumbnail' : ''
              }`}
            >
              <div
                styleName='body-content'
                dangerouslySetInnerHTML={createMarkup(renderedDescription)}
              />
            </Text>
          </div>
        </div>
      </div>
    </div>
  );

  const imagePosition = image && image.position;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div
        styleName={classNames('container', {
          [reskinClassName]: !!reskinClassName,
          fade: transition,
          editing: editMode,
          [`img-${imagePosition}`]: imagePosition,
        })}
        style={{backgroundColor: background}}
        data-automation={`slide-preview${isPreviewMode ? '-thumbnail' : ''}`}
      >
        {imagePosition === 'top' && (
          <ImageContent image={image} editMode={editMode} style={imageStyle} />
        )}
        {textContent}
        {imagePosition === 'bottom' && (
          <ImageContent image={image} editMode={editMode} />
        )}
      </div>
      {button && !isPreviewMode && (
        <PreviewNavButton
          button={button}
          background={background}
          isLightTheme={isButtonLightTheme}
        />
      )}
    </>
  );
};

export default CSSModules(TextSlide, styles, {allowMultiple: true});
